import { UseQueryOptions } from '@tanstack/react-query';

import APIError from 'types/api_error';
import { PricingPlan, PricingPlanExtension } from 'types/pricing_plan';

import { useAuthenticatedQuery } from 'hooks/api/useAuthenticatedQuery';
import { useAPI } from 'hooks/api/useAPI';
import { useCurrentOrganization } from 'hooks/api/useCurrentOrganization';
import { useUser } from 'hooks/useAuth';
import { updateGleapCustomData } from 'services/gleap';

import { Userpilot } from 'userpilot';

type UseCurrentPlanOptions = UseQueryOptions<PricingPlan | null, APIError>;

export function useCurrentPlan({ enabled }: UseCurrentPlanOptions = {}) {
  const { client } = useAPI();
  const user = useUser();
  const { data: organization } = useCurrentOrganization();

  const organizationId = organization?.id;

  const queryResult = useAuthenticatedQuery(
    ['pricing-plan', organizationId],
    async () => {
      if (!organizationId) {
        return Promise.reject(new Error('No organization id provided.'));
      }

      const { data } = await client.get<PricingPlan | null>(
        `/internal/orgs/${organizationId}/plan`
      );

      if (!data?.description) return data;

      // Third-party identification; log user's plan.
      if (user && data) {
        const traits = {
          planName: data.name,
        };

        Userpilot.identify(user.id, traits);
        updateGleapCustomData(traits);
      }

      // Attempt to parse the plan description as a JSON object
      // If it fails, return the plan as is
      try {
        const parsedExtension = JSON.parse(
          data.description
        ) as PricingPlanExtension;
        return {
          ...data,
          description: parsedExtension.tagline,
          extension: parsedExtension,
        };
      } catch {
        return data;
      }
    },
    {
      enabled:
        enabled !== undefined
          ? Boolean(organizationId) && enabled
          : Boolean(organizationId),
    }
  );

  const isSimplifiedUxPlan = queryResult.data?.extension?.simplified_ux;

  const isRingPlan = queryResult.data?.name.includes('Ring');

  return {
    ...queryResult,
    isSimplifiedUxPlan,
    isRingPlan,
  };
}
